import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../configuration/setup/store';
import { AppState } from '../../configuration/types';
import { fetchAccountsThunk, fetchUserThunk, patchLocaleThunk, patchUserThunk, switchAccountThunk } from './thunks';

export const initialState: AppState = {
    user: {
        id: '',
        accountId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        groupIds: [],
        preferredLogin: '',
        preferredLanguage: '',
    },
    accounts: [],
    sessionExpiredAcknowledged: false,
    showSettingsDialog: false,
    pending: false,
    selectedTab: 0,
    switchAccountFailed: false,
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        hideSessionExpiredDialog: (state) => {
            state.sessionExpiredAcknowledged = true;
        },
        showSettingsDialog: (state, action: PayloadAction<number | undefined>) => {
            state.showSettingsDialog = true;
            if (action.payload) {
                state.selectedTab = action.payload;
            } else {
                state.selectedTab = 0;
            }
        },
        hideSettingsDialog: (state) => {
            state.showSettingsDialog = false;
        },
        selectTab: (state, action: PayloadAction<number>) => {
            state.selectedTab = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch user
            .addCase(fetchUserThunk.pending, (state) => {
                state.pending = true;
            })
            .addCase(fetchUserThunk.fulfilled, (state, action) => {
                state.pending = false;
                state.user = action.payload;
            })
            .addCase(fetchUserThunk.rejected, (state) => {
                state.pending = false;
            })

            // fetch accounts
            .addCase(fetchAccountsThunk.pending, (state) => {
                state.pending = true;
            })
            .addCase(fetchAccountsThunk.fulfilled, (state, action) => {
                state.pending = false;
                state.accounts = action.payload;
            })
            .addCase(fetchAccountsThunk.rejected, (state) => {
                state.pending = false;
            })

            // switch account
            .addCase(switchAccountThunk.pending, (state) => {
                state.pending = true;
                state.switchAccountFailed = false;
            })
            .addCase(switchAccountThunk.fulfilled, (state, action) => {
                state.pending = false;
            })
            .addCase(switchAccountThunk.rejected, (state) => {
                state.pending = false;
                state.switchAccountFailed = true;
            })

            // patch user
            .addCase(patchUserThunk.pending, (state) => {
                state.pending = true;
            })
            .addCase(patchUserThunk.fulfilled, (state, action) => {
                state.pending = false;
                state.user = action.payload;
            })
            .addCase(patchUserThunk.rejected, (state) => {
                state.pending = false;
            })

            // patch locale
            .addCase(patchLocaleThunk.pending, (state) => {
                state.pending = true;
            })
            .addCase(patchLocaleThunk.fulfilled, (state, action) => {
                state.pending = false;
            })
            .addCase(patchLocaleThunk.rejected, (state) => {
                state.pending = false;
                state.switchAccountFailed = true;
            })
            // default
            .addDefaultCase((state) => {});
    },
});

export const appReducer = appSlice.reducer;
export const { hideSessionExpiredDialog, showSettingsDialog, hideSettingsDialog, selectTab } = appSlice.actions;

// ------------------------------
// Selectors

export const getSessionExpiredAcknowledged = (state: RootState) => state.app.sessionExpiredAcknowledged;

export const selectUser = (state: RootState) => state.app.user;

export const selectAccounts = (state: RootState) => state.app.accounts;

export const selectPending = (state: RootState) => state.app.pending;

export const selectSwitchAccountFailed = (state: RootState) => state.app.switchAccountFailed;

export const selectShowSettingsDialog = (state: RootState) => state.app.showSettingsDialog;

export const selectTabIndex = (state: RootState) => state.app.selectedTab;

export const isMaintenanceMode = (state: RootState) => false;

export const getAccountsAndCurrentAccount = createSelector([selectAccounts], (accounts) => {
    const currentAccount = accounts.find((acc: { active: any }) => acc.active);
    return {
        accounts,
        currentAccount,
    };
});
